import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, map, of, switchMap, withLatestFrom} from 'rxjs';

import {HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {ErrorHandlerUtils} from '@app/core/utils/error-handler.utils';
import {ToastMessageUtils} from '@app/core/utils/toast-message.utils';
import {EToastMessageOptions, EToastMessageSeverity} from '@app/shared/enums/toast-message.enum';
import {IProduct} from '@app/shared/models/product.model';
import {selectCustomerOrganizationId} from '@app/shared/state/header/header.selector';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {MessageService} from 'primeng/api';
import {NewOrderService} from '../services/new-order.service';
import {
  loadProduct,
  loadProductById,
  LoadProductByIdFailure,
  LoadProductByIdSuccess,
  loadProductSuccess,
  NewOrderActions,
} from './new-order.actions';
@Injectable()
export class NewOrderEffects {
  private readonly actions$ = inject(Actions);
  private readonly newOrderService = inject(NewOrderService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  globalToast = 'global-toast';

  loadProductById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewOrderActions.LoadProductById),
      withLatestFrom(this.store.select(selectCustomerOrganizationId)),
      switchMap(([action, organizationId]: [ReturnType<typeof loadProductById>, number | undefined | null]) =>
        this.loadProductById(action.productId ?? 0, organizationId ?? 0, action.productStatus)
      )
    )
  );

  loadProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewOrderActions.LoadProduct, NewOrderActions.LoadProductFromHeaderEffects),
      withLatestFrom(this.store.select(selectCustomerOrganizationId)),
      switchMap(([action, organizationId]: [ReturnType<typeof loadProduct>, number | undefined | null]) =>
        this.loadProduct(organizationId ?? 0, action.productStatus)
      )
    )
  );

  private loadProductById(productId: number, organizationId: number, productStatus: number) {
    return this.newOrderService.getProductById(productId, organizationId, productStatus)?.pipe(
      map((productResponse: IProduct[]) => {
        return LoadProductByIdSuccess({products: productResponse});
      }),
      catchError((error: HttpErrorResponse) => {
        const errMsg = ErrorHandlerUtils.getErrorMessage(this.translate, error.status);
        ToastMessageUtils.showMessage({
          messageService: this.messageService,
          toastKey: this.globalToast,
          severity: EToastMessageSeverity.Error,
          messageDetail: errMsg,
          closable: true,
          life: EToastMessageOptions.LifeTime,
        });
        setTimeout(() => {
          this.router.navigate(['/category']);
        }, 1000);
        return of(LoadProductByIdFailure({reason: errMsg}));
      })
    );
  }

  private loadProduct(organizationId: number, productStatus: number) {
    return this.newOrderService.getProduct(organizationId, productStatus)?.pipe(
      map((productResponse: IProduct[]) => {
        return loadProductSuccess({products: productResponse});
      })
    );
  }
}
